'use client'

import { Loader2, TriangleAlert } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { EngineLogo } from '@/components/shared/engine-logo'
import { getEngineStatusLabel } from '@/components/dashboard/engine-status-dot'
import { ENGINE_LABELS } from '@/lib/ai/constants'
import type { Engine } from '@/lib/ai/constants'
import { OFFERED_ENGINES } from '@/lib/billing/constants'
import type { PromptEngineStatus } from '@/lib/runs/helpers'
import type { EngineOutcome } from '@/lib/runs/engine-outcome'
import { cn } from '@/lib/utils'

const OUTCOME_LABEL: Record<EngineOutcome, string> = {
  cited: 'Cited',
  named: 'Named',
  not_named: 'Not named',
  failed: 'Failed',
  not_checked: 'Not checked',
}

/**
 * Label tone per outcome (Claude Design `PromptsTable.jsx`): a positive result
 * reads in foreground weight, a failed check in the danger tone, and the two
 * "nothing here" states stay muted — so "Failed" never reads like "Not named".
 * The icon badge still carries the same signal (DES-38: colour is not alone).
 */
const OUTCOME_LABEL_CLASS: Record<EngineOutcome, string> = {
  cited: 'font-semibold text-foreground',
  named: 'font-semibold text-foreground',
  not_named: 'text-muted-foreground',
  failed: 'font-semibold text-destructive',
  not_checked: 'text-muted-foreground',
}

interface EngineIndicatorProps {
  engine: Engine
  /**
   * The legacy mention-status vocabulary. Still the only thing rendered when
   * `outcome` is omitted — every pre-R3.16 call site keeps this exact render.
   */
  status?: PromptEngineStatus
  /**
   * The 5-state outcome vocabulary (R3.16 Prompts list/detail). When set,
   * this takes over rendering from `status` entirely — pass one or the
   * other, never rely on both being read.
   */
  outcome?: EngineOutcome
  /** Renders the outcome label text beside the icon. Only affects the `outcome` path. Default false. */
  showLabel?: boolean
  /** Smaller icon, no label — the run-log's inline ×3 row. Only affects the `outcome` path. Default false. */
  compact?: boolean
}

/**
 * Engine logo with opacity/grayscale based on mention status.
 * Green dot overlay = cited, red dot = accuracy issue, spinner = scanning.
 *
 * Extended (R3.16) with an `outcome`-driven render for the Prompts list and
 * detail pages, which need a 5th state (`failed`) the old `PromptEngineStatus`
 * vocabulary conflates with "not checked" (backend-spec.md §12). `failed`
 * renders a distinct triangle-alert glyph, not just a different dot color, so
 * it reads differently from `not_named` even in grayscale (DES-38).
 */
export function EngineIndicator({
  engine,
  status,
  outcome,
  showLabel = false,
  compact = false,
}: EngineIndicatorProps) {
  if (outcome !== undefined) {
    return (
      <EngineOutcomeIndicator
        engine={engine}
        outcome={outcome}
        showLabel={showLabel}
        compact={compact}
      />
    )
  }

  const resolvedStatus = status ?? null
  const isScanning = resolvedStatus === 'pending'
  const isMentioned =
    resolvedStatus === 'mentioned_cited' ||
    resolvedStatus === 'mentioned_only' ||
    resolvedStatus === 'accuracy_issue'
  const isCited = resolvedStatus === 'mentioned_cited'
  const isAccuracy = resolvedStatus === 'accuracy_issue'
  const isCitedNotMentioned = resolvedStatus === 'cited_not_mentioned'

  const statusLabel = isScanning ? 'Scanning…' : getEngineStatusLabel(resolvedStatus)

  return (
    <Tooltip>
      <TooltipTrigger
        className="cursor-default"
        aria-label={`${ENGINE_LABELS[engine]}: ${statusLabel}`}
      >
        <div className="relative flex size-5 shrink-0 items-center justify-center">
          {isScanning ? (
            <Loader2 className="size-4 text-muted-foreground/40 animate-spin" />
          ) : (
            <>
              <EngineLogo
                engine={engine}
                size={16}
                className={cn(
                  'transition-opacity',
                  resolvedStatus === null && 'opacity-15',
                  !isMentioned && resolvedStatus !== null && 'grayscale opacity-20'
                )}
              />
              {isCited && (
                <span className="absolute -bottom-px -right-px size-[6px] rounded-full bg-success ring-[1.5px] ring-background" />
              )}
              {isAccuracy && (
                <span className="absolute -bottom-px -right-px size-[6px] rounded-full bg-destructive ring-[1.5px] ring-background" />
              )}
              {isCitedNotMentioned && (
                <span className="absolute -bottom-px -right-px size-[6px] rounded-full bg-info ring-[1.5px] ring-background" />
              )}
            </>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <span className="font-medium">{ENGINE_LABELS[engine]}:</span> {statusLabel}
      </TooltipContent>
    </Tooltip>
  )
}

interface EngineOutcomeIndicatorProps {
  engine: Engine
  outcome: EngineOutcome
  showLabel: boolean
  compact: boolean
}

function EngineOutcomeIndicator({
  engine,
  outcome,
  showLabel,
  compact,
}: EngineOutcomeIndicatorProps) {
  const isNotChecked = outcome === 'not_checked'
  const isNotNamedLike = outcome === 'not_named' || outcome === 'failed' || isNotChecked
  const logoSize = compact ? 12 : 16
  const containerSize = compact ? 'size-4' : 'size-5'

  return (
    <Tooltip>
      <TooltipTrigger
        className="cursor-default"
        aria-label={`${ENGINE_LABELS[engine]}: ${OUTCOME_LABEL[outcome]}`}
      >
        <div className={cn('flex items-center gap-1.5', showLabel && 'gap-1.5')}>
          <div className={cn('relative flex shrink-0 items-center justify-center', containerSize)}>
            <EngineLogo
              engine={engine}
              size={logoSize}
              className={cn(
                'transition-opacity',
                isNotChecked && 'opacity-15',
                isNotNamedLike && !isNotChecked && 'grayscale opacity-30'
              )}
            />
            {outcome === 'cited' && (
              <span className="absolute -bottom-px -right-px size-[6px] rounded-full bg-success ring-[1.5px] ring-background" />
            )}
            {outcome === 'failed' && (
              <span
                className="absolute -bottom-1 -right-1 flex size-2.5 items-center justify-center rounded-full bg-destructive ring-[1.5px] ring-background"
                aria-hidden="true"
              >
                <TriangleAlert className="size-1.5 text-background" strokeWidth={3} />
              </span>
            )}
          </div>
          {showLabel && (
            <span className={cn('text-caption whitespace-nowrap', OUTCOME_LABEL_CLASS[outcome])}>
              {OUTCOME_LABEL[outcome]}
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <span className="font-medium">{ENGINE_LABELS[engine]}:</span> {OUTCOME_LABEL[outcome]}
      </TooltipContent>
    </Tooltip>
  )
}

interface EnginesRowProps {
  statuses: Record<Engine, PromptEngineStatus>
  /** Which engines to render. Defaults to all ENGINES; onboarding passes only the 3 it runs. */
  engines?: readonly Engine[]
}

/**
 * A row of EngineIndicators.
 */
export function EnginesRow({ statuses, engines = OFFERED_ENGINES }: EnginesRowProps) {
  return (
    <div className="flex items-center gap-3">
      {engines.map((engine) => (
        <EngineIndicator key={engine} engine={engine} status={statuses[engine]} />
      ))}
    </div>
  )
}
